import { useState, useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Country, State, City } from 'country-state-city'
import { FieldRenderer } from '../fields/FieldRenderer'
import type { FieldMeta } from '@crm/shared'
import { createRecord, updateRecord } from '../../api/records'
import { useAutoFocus } from '../../hooks/useAutoFocus'

interface RecordFormDialogProps {
  open: boolean
  onClose: () => void
  onSaved: () => void
  objectId: string
  fields: FieldMeta[]
  fieldPermissions: Record<string, string>
  mode: 'create' | 'edit'
  existingData?: Record<string, unknown>
  existingVersion?: number
  recordId?: string
}

// 默认国家选项（如果数据库没配置就用这个）
const DEFAULT_COUNTRY_OPTIONS = Country.getAllCountries().map((c) => ({ value: c.isoCode, label: `${c.isoCode} - ${c.name}` }))

function getCityOptions(countryCode: string): Array<{ value: string; label: string }> {
  if (!countryCode) return []
  const citySet = new Set<string>()
  const states = State.getStatesOfCountry(countryCode)
  for (const state of states) {
    for (const city of City.getCitiesOfState(countryCode, state.isoCode)) {
      citySet.add(city.name)
    }
  }
  return Array.from(citySet).sort().map((name) => ({ value: name, label: name }))
}

function getFieldConfig(field: FieldMeta): Record<string, string> {
  if (!field.config) return {}
  try {
    return JSON.parse(field.config as unknown as string)
  } catch {
    return {}
  }
}

export function RecordFormDialog({
  open,
  onClose,
  onSaved,
  objectId,
  fields,
  fieldPermissions,
  mode,
  existingData,
  existingVersion,
  recordId,
}: RecordFormDialogProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>(() => {
    if (mode === 'edit' && existingData) return { ...existingData }
    const initial: Record<string, unknown> = {}
    for (const field of fields) {
      if (field.type === 'reverse_lookup') continue
      if (field.default_value) {
        try {
          initial[field.name] = JSON.parse(field.default_value)
        } catch {
          initial[field.name] = field.default_value
        }
      }
    }
    return initial
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState('')
  const contentRef = useAutoFocus<HTMLDivElement>(open)

  const editableFields = useMemo(
    () =>
      fields.filter((f) => {
        if (f.type === 'reverse_lookup') return false
        const perm = fieldPermissions[f.field_id]
        return mode === 'create' || perm === 'read_write' || perm === undefined
      }),
    [fields, fieldPermissions, mode],
  )

  const hasCountryField = useMemo(() => editableFields.some((f) => f.name === 'country_region'), [editableFields])
  const hasCityField = useMemo(() => editableFields.some((f) => f.name === 'city'), [editableFields])

  const sections = useMemo(() => {
    const map = new Map<string, FieldMeta[]>()
    for (const field of editableFields) {
      const section = field.section || 'General'
      if (!map.has(section)) map.set(section, [])
      map.get(section)!.push(field)
    }
    for (const [, sectionFields] of map) {
      sectionFields.sort((a, b) => {
        if ((a.type === 'long_text' || a.type === 'json') && b.type !== 'long_text' && b.type !== 'json') return 1
        if (a.type !== 'long_text' && a.type !== 'json' && (b.type === 'long_text' || b.type === 'json')) return -1
        return 0
      })
    }
    return map
  }, [editableFields])

  const handleChange = useCallback((fieldName: string, value: unknown) => {
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: value }
      if (fieldName === 'country_region' && hasCityField) {
        next.city = ''
      }
      return next
    })
    setErrors((prev) => {
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [hasCityField])

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}
    for (const field of editableFields) {
      if (field.is_required && (formData[field.name] === undefined || formData[field.name] === null || formData[field.name] === '')) {
        newErrors[field.name] = `${field.label} is required`
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [editableFields, formData])

  const handleSave = useCallback(async () => {
    if (!validate()) return
    setSaving(true)
    setApiError('')
    try {
      if (mode === 'create') {
        await createRecord(objectId, formData)
      } else if (recordId) {
        await updateRecord(objectId, recordId, { data: formData, version: existingVersion ?? 1 })
      }
      onSaved()
      onClose()
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save record')
    } finally {
      setSaving(false)
    }
  }, [validate, mode, objectId, recordId, formData, existingVersion, onSaved, onClose])

  const countryValue = formData.country_region as string | undefined
  const cityOptions = useMemo(() => getCityOptions(countryValue ?? ''), [countryValue])

  // 从 field.options 解析选项
  const getOptionsFromField = useCallback((field: FieldMeta): Array<{ value: string; label: string }> => {
    if (!field.options) return []
    try {
      const parsed = JSON.parse(field.options as unknown as string)
      return parsed.options ?? parsed
    } catch {
      return []
    }
  }, [])

  const getDynamicOptions = useCallback((field: FieldMeta): Array<{ value: string; label: string }> | undefined => {
    if (field.name === 'country_region' && field.type === 'select') {
      const fieldOptions = getOptionsFromField(field)
      return fieldOptions.length > 0 ? fieldOptions : DEFAULT_COUNTRY_OPTIONS
    }
    const config = getFieldConfig(field)
    if (config.depends_on === 'country_region' && field.type === 'select') return cityOptions
    return undefined
  }, [cityOptions, getOptionsFromField])

  const isCityDisabled = useCallback((field: FieldMeta): boolean => {
    const config = getFieldConfig(field)
    if (config.depends_on === 'country_region') return !countryValue
    return false
  }, [countryValue])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'create' ? 'New Record' : 'Edit Record'}</DialogTitle>
      <DialogContent dividers ref={contentRef} onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !saving && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
          e.preventDefault()
          handleSave()
        }
      }}>
        {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
        {Array.from(sections.entries()).map(([section, sectionFields]) => (
          <Box key={section} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {section}
            </Typography>
            <Grid container spacing={2}>
              {sectionFields.map((field) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field.field_id}>
                  <FieldRenderer
                    field={field}
                    value={formData[field.name]}
                    mode="edit"
                    onChange={(v) => handleChange(field.name, v)}
                    error={errors[field.name]}
                    dynamicOptions={getDynamicOptions(field)}
                    disabled={isCityDisabled(field)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
